import React, { useEffect, useState } from 'react';
import { isBreakpointLargeOrAbove, useBreakpoint } from '@noths/polaris-client-ribbons-base';
import type { CarouselLink } from '@noths/polaris-client-ribbons-design-system';
import { TextIcon } from '@noths/polaris-client-ribbons-design-system';

import chevronDown from 'src/assets/svg/chevronDown.svg';
import chevronUp from 'src/assets/svg/chevronUp.svg';
import * as styles from 'src/components/organisms/HeaderCopyBlock/styles';
import type { LinkCarouselTrackingLinkSource } from 'src/components/organisms/LinkCarousel';
import { LinkCarousel, LinkCarouselTrackingCategory } from 'src/components/organisms/LinkCarousel';

const toggleButtonId = 'headerCopyBlockToggleButton';
const copyBlockId = 'headerCopyBlockContent';

export interface HeaderCopyBlockProps {
  description?: string;
  heading: string;
  subheading?: string;
  links: CarouselLink[];
  trackingLinkDataSource: LinkCarouselTrackingLinkSource;
}

export const HeaderCopyBlock = ({
  description,
  heading,
  subheading,
  links,
  trackingLinkDataSource,
}: HeaderCopyBlockProps) => {
  const [isOpen, setIsOpen] = useState<null | boolean>(null);
  const breakpoint = useBreakpoint();
  const accordionBehaviorEnabled = isOpen !== null;
  const renderDescription = description && description !== '<p></p>';
  const accordionIsOpen = accordionBehaviorEnabled ? isOpen : undefined;

  useEffect(() => {
    const isLargeDevice = isBreakpointLargeOrAbove(breakpoint);
    setIsOpen(isLargeDevice);
  }, [breakpoint]);

  return (
    <div css={styles.headerCopyBlock}>
      <button css={styles.toggleButton} onClick={() => setIsOpen(!isOpen)}>
        <h1 css={styles.heading}>{heading}</h1>
        <TextIcon
          id={toggleButtonId}
          aria-expanded={accordionIsOpen}
          aria-controls={copyBlockId}
          css={styles.icon}
          icon={isOpen ? chevronUp : chevronDown}
          iconWidth={16}
          iconHeight={16}
        />
      </button>
      <div
        id={copyBlockId}
        css={styles.accordionContent}
        role="region"
        aria-labelledby={toggleButtonId}
        data-open={accordionIsOpen}
      >
        {subheading && <h2>{subheading}</h2>}
        {renderDescription && (
          <div css={styles.copyBlockContent}>
            <div dangerouslySetInnerHTML={{ __html: description }} />
          </div>
        )}
        {links.length > 0 && (
          <section css={styles.linksCarouselSection}>
            <div css={[styles.insetSectionContent, styles.linksCarouselWrapper]}>
              <LinkCarousel
                title="Categories"
                trackingCategory={LinkCarouselTrackingCategory.ChildCategory}
                links={links}
                trackingLinkDataSource={trackingLinkDataSource}
              />
            </div>
          </section>
        )}
      </div>
    </div>
  );
};
